import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import arrowLeft from '@iconify/icons-fa-solid/arrow-left';
import heartIcon from '@iconify/icons-fa-solid/heart';
import timesIcon from '@iconify/icons-fa-solid/times';
import signOutAlt from '@iconify/icons-fa-solid/sign-out-alt';
import gamepad from '@iconify/icons-fa-solid/gamepad';
import Cookies from 'js-cookie';
import Header from './Header/Header';
import Sidebar from './Sidebar/Sidebar';
import Footer from './Footer/Footer';
import BottomTab from './BottomTab/BottomTab';
import { useScreenSize } from '@/hooks';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import { useGames } from '@/Context/GameContext';
import { getSocket, getUID } from '@/utils';
import { C } from '@/utils/constants';
import styles from './Layout.module.css';

/**
 * Layout Component – CRYPTMET theme
 * Main layout: Header, Sidebar (desktop), main content, Footer, BottomTab (mobile)
 */
const Layout = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useScreenSize();
  const isMobileOrTablet = isMobile || isTablet;
  const location = useLocation();
  const navigate = useNavigate();
  const { currentGame, closeGame, showIframe } = useGames();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  // Apply smooth scroll animation on route change
  useSmoothScroll();

  // Auto-logout when token expires or uid cookie is missing
  useEffect(() => {
    const performLogout = () => {
      const socket = getSocket();
      if (socket && socket.connected) {
        try { socket.emit(C.LOGOUT_USER); } catch (e) { /* silent */ }
      }
      Cookies.remove('token');
      Cookies.remove('uid');
      Cookies.remove('session');
      Cookies.remove('auth');
      try { localStorage.clear(); } catch (e) { /* silent */ }
      try { sessionStorage.clear(); } catch (e) { /* silent */ }
      window.location.href = '/';
    };

    const isLoggedIn = !!localStorage.getItem('token');

    // If logged in but uid cookie is gone, force logout
    if (isLoggedIn && !getUID()) {
      console.log('[Auth] uid cookie missing, auto logging out');
      performLogout();
      return;
    }

    // If token has an expiry, schedule auto-logout
    const tokenExp = localStorage.getItem('tokenExp');
    if (!tokenExp) return;

    const expMs = Number(tokenExp);
    const now = Date.now();

    if (now >= expMs) {
      console.log('[Auth] Token expired, auto logging out');
      performLogout();
      return;
    }

    const timer = setTimeout(() => {
      console.log('[Auth] Token just expired, auto logging out');
      performLogout();
    }, expMs - now);
    return () => clearTimeout(timer);
  }, []);

  // Check if current route is a game route
  const isGameRoute = location.pathname.match(/^\/[^/]+\/[^/]+$/) &&
                      !location.pathname.startsWith('/sports/') &&
                      !location.pathname.startsWith('/match/') &&
                      !location.pathname.startsWith('/menu') &&
                      !location.pathname.startsWith('/affiliate') &&
                      !location.pathname.startsWith('/casino') &&
                      !location.pathname.startsWith('/settings') &&
                      !location.pathname.startsWith('/promotions') &&
                      !location.pathname.startsWith('/vip-club') &&
                      !location.pathname.startsWith('/bonus-page') &&
                      !location.pathname.startsWith('/support') &&
                      !location.pathname.startsWith('/gift-cards') &&
                      !location.pathname.startsWith('/provably-fair') &&
                      !location.pathname.startsWith('/trade');

  // Check if current route is profile-menu
  const isProfileMenuRoute = location.pathname === '/profile-menu';

  // Check if current route is sports-related (no sidebar)
  const isSportsRoute = location.pathname.startsWith('/sports') ||
                        location.pathname.startsWith('/match/') ||
                        location.pathname.startsWith('/menu');

  const handleMenuToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleSidebarItemClick = () => {};

  const handleGameBack = () => {
    closeGame(navigate);
  };

  return (
    <div className={styles.layout}>
      {/* Regular Header - Hide on mobile when game is active or on profile-menu */}
      {!(isMobile && showIframe && isGameRoute) && !isProfileMenuRoute && (
        <Header
          onMenuToggle={handleMenuToggle}
          isSidebarCollapsed={isSidebarCollapsed}
        />
      )}

      <div className={styles.contentWrapper}>
        {/* Sidebar (Desktop only) */}
        {!isMobileOrTablet && !isProfileMenuRoute && (
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            onToggle={handleMenuToggle}
            onItemClick={handleSidebarItemClick}
          />
        )}

        <div
          className={`${styles.mainContentArea} ${isSidebarCollapsed && !isMobileOrTablet ? styles.mainContentAreaCollapsed : ''}`}
        >
          {/* Main Content */}
          <main className={`${styles.main} ${isGameRoute && showIframe ? styles.gameMain : ''}`}>
            <Outlet />
          </main>

          {/* Footer */}
          {!isSportsRoute && !isGameRoute && !isProfileMenuRoute && <Footer />}
        </div>
      </div>

      {/* Bottom Tab Navigation (Mobile only) - Hide when game is active or on profile-menu */}
      {!(isMobile && isGameRoute && showIframe) && !isProfileMenuRoute && <BottomTab />}

      {/* Mobile Game Bottom Bar - Only show on game route when game is active */}
      {isMobile && isGameRoute && showIframe && (
        <div className={styles.gameBottomBar}>
          <button
            className={styles.gameBottomBtn}
            onClick={() => setShowExitModal(true)}
            aria-label="Exit game"
          >
            <Icon icon={arrowLeft} width={16} height={16} />
          </button>

          <button
            className={`${styles.gameBottomBtn} ${isFavorited ? styles.favorited : ''}`}
            onClick={() => setIsFavorited(!isFavorited)}
            aria-label="Favorite"
          >
            <Icon icon={heartIcon} width={16} height={16} style={{ color: isFavorited ? '#ef4444' : 'currentColor' }} />
          </button>

          <span className={styles.gameName}>
            {currentGame?.title || 'Game'}
          </span>

          <div className={styles.inPlayBadge}>
            <span className={styles.inPlayDot} />
            <span>{t('common.inPlay')}</span>
          </div>
        </div>
      )}

      {/* Exit Game Confirmation Modal */}
      {showExitModal && (
        <div className={styles.exitOverlay} onClick={() => setShowExitModal(false)}>
          <div className={styles.exitModal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.exitCloseBtn} onClick={() => setShowExitModal(false)}>
              <Icon icon={timesIcon} width={14} height={14} />
            </button>
            <div className={styles.exitIconWrap}>
              <Icon icon={gamepad} width={28} height={28} />
            </div>
            <h3 className={styles.exitTitle}>{t('common.exitGame')}</h3>
            <p className={styles.exitText}>{t('common.exitGameConfirm')}</p>
            <div className={styles.exitActions}>
              <button className={styles.exitCancelBtn} onClick={() => setShowExitModal(false)}>
                {t('common.continuePlaying')}
              </button>
              <button className={styles.exitConfirmBtn} onClick={() => { setShowExitModal(false); handleGameBack(); }}>
                <Icon icon={signOutAlt} width={14} height={14} />
                {t('common.exitGame')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;